<template>
  <div
    class="api-key-docs bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6 mt-8"
  >
    <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-4">
      Using Your API Keys
    </h3>

    <div class="space-y-6">
      <!-- Authentication Section -->
      <div>
        <h4 class="text-lg font-medium text-gray-900 dark:text-gray-200 mb-2">
          Authentication
        </h4>
        <p class="text-gray-600 dark:text-gray-400 mb-3">
          To authenticate your API requests, include your API key in the request
          headers:
        </p>
        <div
          class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 font-mono text-sm relative group overflow-x-auto"
        >
          <button
            class="absolute top-2 right-2 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
            :title="copied === 'auth' ? 'Copied!' : 'Copy to clipboard'"
            @click="copyToClipboard('X-API-Key: YOUR_API_KEY')"
          >
            <Icon
              :path="copied === 'auth' ? mdiCheck : mdiContentCopy"
              class="w-5 h-5"
            />
          </button>
          <code class="text-gray-800 dark:text-gray-200">
            X-API-Key: YOUR_API_KEY
          </code>
        </div>
      </div>

      <!-- Base URL Section -->
      <div>
        <h4 class="text-lg font-medium text-gray-900 dark:text-gray-200 mb-2">
          Base URL
        </h4>
        <div
          class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 font-mono text-sm relative group overflow-x-auto"
        >
          <button
            class="absolute top-2 right-2 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
            :title="copied === 'url' ? 'Copied!' : 'Copy to clipboard'"
            @click="copyToClipboard('https://api.kappaml.com/v1')"
          >
            <Icon
              :path="copied === 'url' ? mdiCheck : mdiContentCopy"
              class="w-5 h-5"
            />
          </button>
          <code class="text-gray-800 dark:text-gray-200">
            https://api.kappaml.com/v1
          </code>
        </div>
      </div>

      <!-- Example Usage Section -->
      <div>
        <h4 class="text-lg font-medium text-gray-900 dark:text-gray-200 mb-2">
          Example Usage
        </h4>
        <div class="space-y-4">
          <!-- List Models Example -->
          <div>
            <p class="text-gray-600 dark:text-gray-400 mb-2">
              List all models:
            </p>
            <div
              class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 font-mono text-sm relative group overflow-x-auto"
            >
              <button
                class="absolute top-2 right-2 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                :title="copied === 'list' ? 'Copied!' : 'Copy to clipboard'"
                @click="copyToClipboard(listModelCommand)"
              >
                <Icon
                  :path="copied === 'list' ? mdiCheck : mdiContentCopy"
                  class="w-5 h-5"
                />
              </button>
              <pre class="text-gray-800 dark:text-gray-200 whitespace-pre">
curl -X GET https://api.kappaml.com/v1/models \
  -H "X-API-Key: YOUR_API_KEY" \
  -H "Content-Type: application/json"</pre
              >
            </div>
          </div>

          <!-- Create Model Example -->
          <div>
            <p class="text-gray-600 dark:text-gray-400 mb-2">
              Create a new model:
            </p>
            <div
              class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 font-mono text-sm relative group overflow-x-auto"
            >
              <button
                class="absolute top-2 right-2 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                :title="copied === 'create' ? 'Copied!' : 'Copy to clipboard'"
                @click="copyToClipboard(createModelCommand)"
              >
                <Icon
                  :path="copied === 'create' ? mdiCheck : mdiContentCopy"
                  class="w-5 h-5"
                />
              </button>
              <pre class="text-gray-800 dark:text-gray-200 whitespace-pre">
curl -X POST https://api.kappaml.com/v1/models \
  -H "X-API-Key: YOUR_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "name": "My Model",
    "ml_type": "classification"
  }'</pre
              >
            </div>
          </div>
        </div>
      </div>

      <!-- API Documentation -->
      <div>
        <h4 class="text-lg font-medium text-gray-900 dark:text-gray-200 mb-2">
          API Documentation
        </h4>
        <p class="text-gray-600 dark:text-gray-400 mb-3">
          For detailed API documentation, including all available endpoints and
          request/response formats, visit:
        </p>
        <a
          href="https://api.kappaml.com/docs"
          target="_blank"
          rel="noopener noreferrer"
          class="text-primary hover:text-primary/80 font-medium flex items-center gap-2"
        >
          <span>View Full API Documentation</span>
          <Icon :path="mdiOpenInNew" class="w-5 h-5" />
        </a>
      </div>

      <!-- Rate Limits Section -->
      <div>
        <h4 class="text-lg font-medium text-gray-900 dark:text-gray-200 mb-2">
          Rate Limits
        </h4>
        <p class="text-gray-600 dark:text-gray-400">
          API requests are subject to rate limiting. The current limits are:
        </p>
        <ul class="list-disc list-inside text-gray-600 dark:text-gray-400 mt-2">
          <li>300 requests per minute per API key</li>
        </ul>
        <p class="text-gray-600 dark:text-gray-400 mt-2">
          Need higher rate limits?
          <a
            href="https://www.kappaml.com/contact"
            target="_blank"
            rel="noopener noreferrer"
            class="text-primary hover:text-primary/80"
            >Contact us</a
          >
          to discuss possible options.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from "@/components/Icon.vue";
import { mdiOpenInNew, mdiContentCopy, mdiCheck } from "@mdi/js";
import { ref } from "vue";

export default {
  name: "ApiKeyDocs",
  components: {
    Icon,
  },
  setup() {
    const copied = ref("");
    const listModelCommand = `curl -X GET https://api.kappaml.com/v1/models \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json"`;

    const createModelCommand = `curl -X POST https://api.kappaml.com/v1/models \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "My Model",
    "ml_type": "classification"
  }'`;

    const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text);
        // Set the copied state to show the check icon
        const id = text.length > 20 ? text.substring(0, 20) : text;
        copied.value = id;
        // Reset after 2 seconds
        setTimeout(() => {
          if (copied.value === id) {
            copied.value = "";
          }
        }, 2000);
      } catch (err) {
        console.error("Failed to copy text:", err);
      }
    };

    return {
      mdiOpenInNew,
      mdiContentCopy,
      mdiCheck,
      copied,
      copyToClipboard,
      listModelCommand,
      createModelCommand,
    };
  },
};
</script>

<style scoped>
.overflow-x-auto {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS devices */
}

@media (max-width: 640px) {
  pre {
    white-space: pre;
    word-wrap: normal;
  }
}
</style>
